import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, AlertCircle, DollarSign, Target, Layers } from 'lucide-react';

const BettingCalculator = () => {
  const [capital, setCapital] = useState('');
  const [pasos, setPasos] = useState('');
  const [semilla, setSemilla] = useState('');
  const [resultado, setResultado] = useState(null);
  const [proyeccion, setProyeccion] = useState([]);

  // Función para calcular el capital necesario dados pasos y semilla
  const calcularCapital = (semillaVal, pasosVal) => {
    // Capital = semilla * (2^(pasos+1) - 1)
    return semillaVal * (Math.pow(2, pasosVal + 1) - 1);
  };

  // Función para calcular los pasos dados capital y semilla
  const calcularPasos = (capitalVal, semillaVal) => {
    // Despejando: pasos = log2((capital/semilla) + 1) - 1
    const ratio = (capitalVal / semillaVal) + 1;
    return Math.floor(Math.log2(ratio) - 1);
  };

  // Función para calcular la semilla dados capital y pasos
  const calcularSemilla = (capitalVal, pasosVal) => {
    // Despejando: semilla = capital / (2^(pasos+1) - 1)
    return capitalVal / (Math.pow(2, pasosVal + 1) - 1);
  };

  // Función para generar la tabla de proyección
  const generarProyeccion = (semillaVal, pasosVal) => {
    const tabla = [];
    let acumulado = 0;

    for (let i = 0; i <= pasosVal; i++) {
      const apuesta = semillaVal * Math.pow(2, i);
      acumulado += apuesta;
      tabla.push({
        paso: i,
        apuesta: apuesta,
        acumulado: acumulado
      });
    }

    return tabla;
  };

  // Efecto para calcular automáticamente cuando hay 2 valores
  useEffect(() => {
    const capitalVal = parseFloat(capital);
    const pasosVal = parseInt(pasos);
    const semillaVal = parseFloat(semilla);

    // Determinar qué calcular basado en los inputs
    if (capital && pasos && !semilla) {
      // Calcular semilla
      const semillaCalculada = calcularSemilla(capitalVal, pasosVal);
      if (semillaCalculada > 0 && isFinite(semillaCalculada)) {
        setResultado({
          tipo: 'semilla',
          valor: semillaCalculada,
          mensaje: `Con un capital de $${capitalVal.toLocaleString()} y aguantando ${pasosVal} pasos, necesitas una semilla de:`
        });
        setProyeccion(generarProyeccion(semillaCalculada, pasosVal));
      } else {
        setResultado({
          tipo: 'error',
          mensaje: 'Los valores ingresados no son válidos. El capital es insuficiente para esos pasos.'
        });
        setProyeccion([]);
      }
    } else if (capital && semilla && !pasos) {
      // Calcular pasos
      const pasosCalculados = calcularPasos(capitalVal, semillaVal);
      if (pasosCalculados >= 0 && isFinite(pasosCalculados)) {
        setResultado({
          tipo: 'pasos',
          valor: pasosCalculados,
          mensaje: `Con un capital de $${capitalVal.toLocaleString()} y una semilla de $${semillaVal.toLocaleString()}, podrás aguantar:`
        });
        setProyeccion(generarProyeccion(semillaVal, pasosCalculados));
      } else {
        setResultado({
          tipo: 'error',
          mensaje: 'Los valores ingresados no son válidos. La semilla es mayor que el capital.'
        });
        setProyeccion([]);
      }
    } else if (pasos && semilla && !capital) {
      // Calcular capital
      const capitalCalculado = calcularCapital(semillaVal, pasosVal);
      if (capitalCalculado > 0 && isFinite(capitalCalculado)) {
        setResultado({
          tipo: 'capital',
          valor: capitalCalculado,
          mensaje: `Para aguantar ${pasosVal} pasos con una semilla de $${semillaVal.toLocaleString()}, necesitas un capital de:`
        });
        setProyeccion(generarProyeccion(semillaVal, pasosVal));
      } else {
        setResultado({
          tipo: 'error',
          mensaje: 'Los valores ingresados no son válidos.'
        });
        setProyeccion([]);
      }
    } else if (capital && pasos && semilla) {
      // Si están los 3 valores, mostrar validación
      const capitalNecesario = calcularCapital(semillaVal, pasosVal);
      const diferencia = capitalVal - capitalNecesario;

      if (Math.abs(diferencia) < 0.01) {
        setResultado({
          tipo: 'validacion',
          valido: true,
          mensaje: '✓ Los valores son consistentes entre sí'
        });
      } else if (diferencia > 0) {
        setResultado({
          tipo: 'validacion',
          valido: true,
          mensaje: `✓ Capital suficiente. Te sobrarían $${diferencia.toLocaleString()}`
        });
      } else {
        setResultado({
          tipo: 'validacion',
          valido: false,
          mensaje: `✗ Capital insuficiente. Te faltan $${Math.abs(diferencia).toLocaleString()}`
        });
      }
      setProyeccion(generarProyeccion(semillaVal, pasosVal));
    } else {
      setResultado(null);
      setProyeccion([]);
    }
  }, [capital, pasos, semilla]);

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const limpiarCampos = () => {
    setCapital('');
    setPasos('');
    setSemilla('');
    setResultado(null);
    setProyeccion([]);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Calculadora de Apuestas Progresivas
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ingresa dos valores cualesquiera para calcular el tercero
            </p>
          </div>
        </div>

        {/* Inputs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Capital */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <DollarSign className="w-4 h-4 text-success-500" />
              <span>Capital</span>
            </label>
            <input
              type="number"
              value={capital}
              onChange={(e) => setCapital(e.target.value)}
              placeholder="Ej: 100000"
              className="w-full px-4 py-2 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              min="0"
              step="1000"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Dinero disponible total
            </p>
          </div>

          {/* Pasos */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Layers className="w-4 h-4 text-primary-500" />
              <span>Pasos</span>
            </label>
            <input
              type="number"
              value={pasos}
              onChange={(e) => setPasos(e.target.value)}
              placeholder="Ej: 10"
              className="w-full px-4 py-2 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              min="0"
              step="1"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Número de pasos a aguantar
            </p>
          </div>

          {/* Semilla */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Target className="w-4 h-4 text-warning-500" />
              <span>Semilla</span>
            </label>
            <input
              type="number"
              value={semilla}
              onChange={(e) => setSemilla(e.target.value)}
              placeholder="Ej: 1000"
              className="w-full px-4 py-2 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              min="0"
              step="100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Apuesta inicial
            </p>
          </div>
        </div>

        {/* Botón Limpiar */}
        <div className="flex justify-end">
          <button
            onClick={limpiarCampos}
            className="btn-secondary text-sm"
          >
            Limpiar Campos
          </button>
        </div>

        {/* Resultado */}
        {resultado && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-6"
          >
            {resultado.tipo === 'error' ? (
              <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-danger-600 dark:text-danger-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-danger-800 dark:text-danger-300">
                      Error en el cálculo
                    </p>
                    <p className="text-sm text-danger-700 dark:text-danger-400 mt-1">
                      {resultado.mensaje}
                    </p>
                  </div>
                </div>
              </div>
            ) : resultado.tipo === 'validacion' ? (
              <div className={`p-4 border rounded-lg ${
                resultado.valido
                  ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                  : 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800'
              }`}>
                <p className={`text-sm font-medium ${
                  resultado.valido
                    ? 'text-success-800 dark:text-success-300'
                    : 'text-warning-800 dark:text-warning-300'
                }`}>
                  {resultado.mensaje}
                </p>
              </div>
            ) : (
              <div className="p-6 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {resultado.mensaje}
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    {resultado.tipo === 'capital' && <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400" />}
                    {resultado.tipo === 'pasos' && <Layers className="w-6 h-6 text-primary-600 dark:text-primary-400" />}
                    {resultado.tipo === 'semilla' && <Target className="w-6 h-6 text-warning-600 dark:text-warning-400" />}
                    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {resultado.tipo === 'pasos'
                        ? `${resultado.valor} pasos`
                        : formatCurrency(resultado.valor)
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Tabla de Proyección */}
      {proyeccion.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Proyección de Pasos
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead>
                <tr className="bg-gray-50 dark:bg-dark-800">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Paso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Apuesta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Capital Acumulado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Multiplicador
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-900 divide-y divide-gray-200 dark:divide-dark-700">
                {proyeccion.map((item, index) => (
                  <motion.tr
                    key={item.paso}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-semibold text-sm">
                        {item.paso}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(item.apuesta)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatCurrency(item.acumulado)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-primary text-white">
                        x{Math.pow(2, item.paso)}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Resumen final */}
          <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-dark-800 dark:to-dark-700 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total de Pasos</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {proyeccion.length}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Última Apuesta</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(proyeccion[proyeccion.length - 1]?.apuesta)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Capital Total Requerido</p>
                <p className="text-lg font-bold text-success-600 dark:text-success-400">
                  {formatCurrency(proyeccion[proyeccion.length - 1]?.acumulado)}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Información adicional */}
      <div className="card p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium">¿Cómo funciona?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
              <li>Cada paso duplica la apuesta anterior (sistema Martingala)</li>
              <li>El capital acumulado es la suma de todas las apuestas hasta ese paso</li>
              <li>Formula: Capital = Semilla × (2<sup>(Pasos+1)</sup> - 1)</li>
              <li>Ingresa cualquier 2 valores y la calculadora determinará el tercero automáticamente</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BettingCalculator;
